import EventEmitter from 'events';

import { API } from '../utils/api';

import { createAdSlot, logOnServerDebugInfo, replaceBaseURLMacroses } from '../utils/utils';
import {
  APICallMethod,
  AdUnitStatus,
  AdFormat,
  ContentType,
  event,
  eventAssociator,
  Platform,
  TagsGroup,
  ACTIVE_UNIT_STATUSES, PlayerType
} from '../utils/const';

class AdUnit {
  constructor(cfg) {
    this.config = cfg;
    const { playerNode, emitter, logger, queueManager, impressionDetector } = cfg;

    this.timers = {
      startAd: null,
      initVPAID: null,
      checkAlive: null,
      checkVolume: null,
      reuse: null,
    };

    this.emitter = emitter;
    this.logger = logger;
    this.queueManager = queueManager;
    this.localEmitter = new EventEmitter();
    this.impressionDetector = impressionDetector;

    this.advID = null;
    this.impressionEmitted = false;
    this.muted = true;
    this.slot = createAdSlot();
    this.inFrame = false;
    playerNode.appendChild(this.slot);

    this.debug_vis = false;

    this._status = AdUnitStatus.INIT;
    this._startTimestamp = null;
    this._updateGUIRunning = false;

    this._subscribeEvents();

    if (this.config.playerConfig.ws) {
      this.emitter.on(event.Player.SocketConnectionUpdate, (socket) => { this.config.socket = socket; });
    }
  }

  _subscribeEvents() {
    this.localEmitter.on(event.Ad.AdError, this._onError);
    this.localEmitter.on(event.Ad.AdImpression, this._onImpression);
    this.localEmitter.on(event.Ad.AdVideoStart, this._onVideoStart);
    this.localEmitter.on(event.Ad.AdVideoFirstQuartile, this._onVideoFirstQuartile);
    this.localEmitter.on(event.Ad.AdVideoMidpoint, this._onVideoMidpoint);
    this.localEmitter.on(event.Ad.AdVideoThirdQuartile, this._onVideoThirdQuartile);
    this.localEmitter.on(event.Ad.AdVideoComplete, this._onVideoComplete);
    this.localEmitter.on(event.Ad.AdPaused, this._onVideoPause);
    this.localEmitter.on(event.Ad.AdPlaying, this._onVideoResume);
    this.localEmitter.on(event.Ad.AdClickThru, this._onClickThru);
    this.localEmitter.on(event.Ad.AllAdComplete, this._onAllAdComplete);
  }

  _unsubscribeEvents() {
    this.localEmitter.off(event.Ad.AdError, this._onError);
    this.localEmitter.off(event.Ad.AdImpression, this._onImpression);
    this.localEmitter.off(event.Ad.AdVideoStart, this._onVideoStart);
    this.localEmitter.off(event.Ad.AdVideoFirstQuartile, this._onVideoFirstQuartile);
    this.localEmitter.off(event.Ad.AdVideoMidpoint, this._onVideoMidpoint);
    this.localEmitter.off(event.Ad.AdVideoThirdQuartile, this._onVideoThirdQuartile);
    this.localEmitter.off(event.Ad.AdVideoComplete, this._onVideoComplete);
    this.localEmitter.off(event.Ad.AdPaused, this._onVideoPause);
    this.localEmitter.off(event.Ad.AdPlaying, this._onVideoResume);
    this.localEmitter.off(event.Ad.AdClickThru, this._onClickThru);
    this.localEmitter.off(event.Ad.AllAdComplete, this._onAllAdComplete);
  }

  _updateTimeline(currentTime, duration) {
    const { uiManager } = this.config;
    uiManager.updateTimeline(currentTime, duration);
  }

  // _updateCloseCountdown(elapsedTime) {
  //   return; // DEBUG!!!
  //   const { uiManager } = this.config;
  //
  //   uiManager.updateCloseCountdown(elapsedTime);
  // }

  _updateGUI = () => {
    if (this.status !== AdUnitStatus.PLAYING) return;

    try {
      if (this.getCurrentTime && this.getDuration) {
        this._updateTimeline(this.getCurrentTime(), this.getDuration());
      }
    } catch(e) {}

    requestAnimationFrame(this._updateGUI);
  }

  _trackEvent(evt) {
    let trackingEvent;
    const { trackers, baseTrackers, playerConfig, params } = this.config;
    const { ws } = playerConfig;

    if (evt === event.Ad.AdVolumeChange) { // uncommon case for mute/unmute;
      if (this.getVolume && this.getVolume() === 0) {
        trackingEvent = 'mute';
      } else {
        trackingEvent = 'unmute';
      }
    } else {
      trackingEvent = eventAssociator[evt];
    }

    if (evt === event.Ad.AdImpression && params.platform === Platform.YANDEX && this.impressionEmitted) return;

    if (trackingEvent) {
      if (baseTrackers && !ws) {
        const trackingUrls = baseTrackers[trackingEvent];

        if (trackingUrls) {
          trackingUrls.forEach((url) => this._callTrackEventUrl(url)); // /*if (evt !== event.Ad.AdError || (isViadsTracker && this.config.logErrors) || !isViadsTracker) callTrackEventUrl(url); */
        }
      } else {
        const { socket, playerConfig, params } = this.config;

        const preparedSocketTrackingData = replaceBaseURLMacroses(`type=player&sid=(sid)&tid=(tid)&zone=(zone)&event=rtb&event2=${trackingEvent}&cb=(cb)&vis=(vis)&v=${playerConfig.playerVersion}`, { sid: playerConfig.sid, zone: playerConfig.zone, tid: params.tid });
        socket.send(preparedSocketTrackingData);
      }

      if (trackers) {
        const trackingUrls = trackers[trackingEvent];

        if (trackingUrls) {
          trackingUrls.forEach((url) => this._callTrackEventUrl(url)); // /*if (evt !== event.Ad.AdError || (isViadsTracker && this.config.logErrors) || !isViadsTracker) callTrackEventUrl(url); */
        }
      }
    }
  }

  shouldContentBeShown = ({ impressionsAmount, midrollCounter }) => { // impressionsInCTC
    const { playerConfig } = this.config;

    const { preroll, midroll, type } = playerConfig.content;
    const contentType = playerConfig.content.mode;
    const ignorePrerollCondition = this.queueManager.requests > 2 && impressionsAmount > 0;

    if (type === ContentType.PRELOADER || type === ContentType.NONE) return false;

    const regularContentShowCondition = this.config.params.format === AdFormat.GOOGLE && contentType !== ContentType.STREAM && contentType !== ContentType.RUTUBE; // (this.config.params.format === AdFormat.MIDROLL || this.config.params.format === AdFormat.PREROLL ||
    const prerollShowCondition = preroll === 0 ? false : (impressionsAmount === preroll && !ignorePrerollCondition); // impressionsAmount === preroll;
    const midrollShowCondition = (midrollCounter > 0 && impressionsAmount > preroll && midrollCounter % midroll === 0) && !this.queueManager.ignoreMidrollCondition; // ((impressionsInCTC - (impressionsAmount > impressionsInCTC ? 0 : preroll)) % midroll === 0);
    // const midrollShowCondition = midrollCounter > 0 && midrollCounter % midroll === 0 && !this.queueManager.ignoreMidrollCondition; // ((impressionsInCTC - (impressionsAmount > impressionsInCTC ? 0 : preroll)) % midroll === 0);
    const streamCondition = contentType === ContentType.STREAM && impressionsAmount === preroll; // prerollShowCondition;

    return (regularContentShowCondition || prerollShowCondition || streamCondition || midrollShowCondition);
  }

  _callTrackEventUrl = (url) => {
    const { playerConfig } = this.config;

    // const timestamp = Date.now(); // performance.now();
    // this.impressionDetector.getViewabilityRatio((ratio) => {
    //   // const perf = Date.now() - timestamp;
    //   // console.log('performance: ', perf);
    //   // logOnServerDebugInfo(JSON.stringify({ test: 4, location: window.location.href, performance: perf, visibility: ratio * 100, ua: navigator.userAgent }), this.config.params.tid);
    //
    //   const finalUrl = url.includes(playerConfig.ldom) ? this._replaceURLMacroses(url, true, Math.ceil(v * 100)) + `&v=${playerConfig.playerVersion}` : url;
    //   API.call(finalUrl, {}, { mode: 'no-cors' }, APICallMethod.IMAGE);
    // });
    const finalUrl = url.includes(playerConfig.ldom) ? this._replaceURLMacroses(url, true, Math.ceil(this.impressionDetector.viewabilityRatio * 100)) + `&v=${playerConfig.playerVersion}` : url;
    API.call(finalUrl, {}, { mode: 'no-cors' }, APICallMethod.IMAGE);
  }

  _replaceURLMacroses = (url, useVis = true, visibility) => {
    if (!url.includes('(random)') && !url.includes('(cb)') && url.includes('?')) url += "&cbb=(random)";

    // const playerWidth = parseInt(window.top.getComputedStyle(this.slot.parentElement).getPropertyValue(`--${CSS_PREFIX}player-width`));
    // const playerHeight = parseInt(window.top.getComputedStyle(this.slot.parentElement).getPropertyValue(`--${CSS_PREFIX}player-height`));ч
    if (useVis) {
      if (!url.includes('(vis)') && url.includes(this.config.playerConfig.ldom)) url += '&vis=(vis)';
      url = url.replace(/\(vis\)/ig, visibility);
      // url = url.replace(/\(vis\)/ig, getElementViewabilityPercent(this.slot, this.config.params.platform));
    }

    return url
      .replace(/\(width\)/ig, this.slot.clientWidth) //  || playerWidth
      .replace(/\(height\)/ig, this.slot.clientHeight) // || playerHeight
      .replace(/\(region\)/ig, this.config.playerConfig.region)
      // .replace(/\(views\)/ig, this.config.queueManager.impressions)
      .replace(/\(views\)/ig, ACTIVE_UNIT_STATUSES.includes(this.status) ? this.config.queueManager.startAttempts : 0)
      .replace(/\(tg\)/ig, Math.floor((Date.now() - this.config.queueManager.initTimestamp) / 1000))
      .replace(/\(tu\)/ig, Math.floor((Date.now() - (ACTIVE_UNIT_STATUSES.includes(this.status) ? this._startTimestamp : this.queueManager.tagsResponseTimestamp)) / 1000))
      .replace(/\(ecode\)/ig, this.errorCode)
      // .replace(/\(ecode\)/ig, encodeURIComponent(this.lastError))
      .replace(/\(random\)|\(cb\)/ig, Date.now());
  }

  _runUpdateGUI = () => {
    if (!this._updateGUIRunning) {
      this._updateGUIRunning = true;
      requestAnimationFrame(this._updateGUI);
    }
  }

  _onClickThru = () => {
    this.logger.log(`[AdUnit ${this.config.params.groupId === TagsGroup.CONTENT ? '(C) ' : ''}- ${this.config.playerConfig.zone} / ${this.config.params.tid}] _onClickThru`, this);
    this._trackEvent(event.Ad.AdClickThru);
  }

  _onError = ({ code = -1 } = {}) => {
    if (!document.body.contains(this.slot) || this.status === AdUnitStatus.ERROR) return;

    this.errorCode = `err_${code}`;

    this.logger.log(`[AdUnit ${this.config.params.groupId === TagsGroup.CONTENT ? '(C) ' : ''}- ${this.config.playerConfig.zone} / ${this.config.params.tid}] _onError: code ${this.errorCode}`, this);

    if (this.config.params.groupId === TagsGroup.CONTENT && ACTIVE_UNIT_STATUSES.includes(this.status)) this.emitter.emit(event.TagsContent.EmitEvent, { unit: this, evt: event.Ad.AdError });

    window.vadsCounter.errors += 1;

    Object.values(this.timers).forEach(timeout => clearTimeout(timeout));
    this.config.queueManager.queue[TagsGroup.IN_PROCESS].filter(unit => unit !== this);

    const { groupId } = this.config.params;
    if (groupId === TagsGroup.YANDEX || groupId === TagsGroup.ADMAN) {
      if (this.queueManager.loadedUnitsExistInGroup(groupId)) {
        this.timers.reuse = setTimeout(() => {
          this.logger.log(`[AdUnit ${this.config.params.groupId === TagsGroup.CONTENT ? '(C) ' : ''}- ${this.config.playerConfig.zone} / ${this.config.params.tid}] reuse timeout`, this);
          this.queueManager.reuseUnit(this);
        }, this.config.params.settings.reload * 1000 || 60000);
      } else {
        this.queueManager.reuseUnitsGroup(groupId);

        if (groupId === TagsGroup.YANDEX) {
          this.queueManager.updateGroupsOrder(1);
        }
      }
    }
    // else if (groupId === TagsGroup.CONTENT) {
    //   this.queueManager.reuseUnit(this);
    // }

    // if (ACTIVE_UNIT_STATUSES.includes(this.status)) {
    //   this.emitter.emit(event.Player.ToggleControls, true);
    // }

    if ((this.status === AdUnitStatus.PLAYING && this.config.playerConfig.content.type === ContentType.NONE) || this.queueManager.prevActiveUnit === this) {
      this.emitter.emit(event.Player.PlayerVisibilityChangeRequest, false);
    }

    const { extraEvents } = this.config.playerConfig;
    if (extraEvents && extraEvents.includes('err') && code !== 10) { // TODO: debug
      this._trackEvent(event.Ad.AdError);
    }

    this.status = AdUnitStatus.ERROR;
    this.remove();
  }

  _onImpression = () => {
    if (!document.body.contains(this.slot) || this.status === AdUnitStatus.ERROR || this.status === AdUnitStatus.FINISHED) return this.logger.log(`unit ${this.config.params.tid} emitted impression event, but status = ${this.status}`, this);
    if (this.config.params.groupId === TagsGroup.CONTENT && this.queueManager.someAdPlaying()) {
      this.logger.log(`[AdUnit ${this.config.params.groupId === TagsGroup.CONTENT ? '(C) ' : ''}- ${this.config.playerConfig.zone} / ${this.config.params.tid}] content tag unit emitted impression event, but some of primary group are playing`, this);
      this.localEmitter.emit(event.Ad.AdVideoComplete, { isContentUnit: true });

      return;
    }
    this.logger.log(`[AdUnit ${this.config.params.groupId === TagsGroup.CONTENT ? '(C) ' : ''}- ${this.config.playerConfig.zone} / ${this.config.params.tid}] _onImpression`, this);

    window.vadsCounter.impressions += 1;

    if (this.timers.startAd) {
      clearTimeout(this.timers.startAd);
      this.timers.startAd = null;
    }

    this.status = AdUnitStatus.PLAYING;
    // this._startTimestamp = this._startTimestamp || Date.now();

    this.emitter.emit(event.Player.PlayerVisibilityChangeRequest, true);
    this.emitter.emit(event.Ad.AdImpression, { advID: this.advID, unit: this });

    this.impressionDetector.subscribe({
      event: 'onViewabilityIntervalDone',
      callback: this.onViewabilityIntervalDone,
    });

    this.impressionDetector.updateViewabilityRatio();
    this.impressionDetector.resetViewabilityInterval();

    // this.slot.style.backgroundColor = `var(--${IS_FLYADS ? 'fly' : 'vads'}-slot-background)`;

    this._trackEvent(event.Ad.AdImpression);
    this.impressionEmitted = true;

    this._runUpdateGUI();
  }

  onViewabilityIntervalDone = () => {
    if (this.status === AdUnitStatus.ERROR || this.status === AdUnitStatus.FINISHED || !this.slot) return;

    this.debug_vis = true;
    this._trackEvent(event.Player.ViewabilityLog);
  }

  _onVideoStart = () => {
    if (!document.body.contains(this.slot)) return;
    this.logger.log(`[AdUnit ${this.config.params.groupId === TagsGroup.CONTENT ? '(C) ' : ''}- ${this.config.playerConfig.zone} / ${this.config.params.tid}] _onVideoStart`, this);

    this.emitter.emit(event.UI.CloseTimerStart, { isContentUnitStarted: this.config.params.groupId === TagsGroup.CONTENT });
    this.emitter.emit(event.Ad.AdVideoStart, { advID: this.advID, unit: this });

    this.slot.classList.add('playing');

    // this._startTimestamp = this._startTimestamp || Date.now();

    const { playerConfig, uiManager, advInfo } = this.config;
    if ((playerConfig.content.type === ContentType.NONE || playerConfig.content.type === ContentType.PRELOADER) && this.queueManager.prevActiveUnit) { //  && params.platform === Platform.YANDEX
      // this.queueManager.prevActiveUnit.slot.style.position = 'absolute';
      this.queueManager.prevActiveUnit.remove();
      this.queueManager.prevActiveUnit = null;
    }

    if (advInfo) {
      const { advertisementLabel } = uiManager.ui;
      const advertisementLabelLink = advertisementLabel.querySelector('a');
      advertisementLabelLink.href = advInfo.url;
    }

    setTimeout(() => this.slot.classList.add('active'), 0);

    if (this.timers.startAd) {
      clearTimeout(this.timers.startAd);
      this.timers.startAd = null;
    }

    if (this.config.params.platform === Platform.YANDEX || this.config.params.platform === Platform.ADMAN || this.config.params.platform === Platform.VK || this.config.params.platform === Platform.GNEZDO) {
      this.emitter.emit(event.Player.ToggleControls, false);
    } else {
      this.emitter.emit(event.Player.ToggleControls, true);
    }

    // this.emitter.emit(event.Player.ToggleControls, ![Platform.YANDEX, Platform.ADMAN, Platform.VK].includes(this.config.params.platform));

    if (this.config.params.groupId === TagsGroup.CONTENT) this.emitter.emit(event.TagsContent.EmitEvent, { unit: this, evt: event.Ad.AdVideoStart });
    this.emitter.emit(event.Player.PlayerVisibilityChangeRequest, true);
    this.emitter.emit(event.Player.ExpandRequest);
    this.emitter.emit(event.Player.ContentFinishRequest, { keepContentVisible: false });
    // this.emitter.emit(event.Ad.AdVideoStart, { advID: this.advID, unit: this });
    if (this.config.playerConfig.type === PlayerType.EMBED || this.config.playerConfig.type === PlayerType.STREAM) this.emitter.emit(event.UI.HidePoster);

    // const { guiSlot, hidePoster } = uiManager;
    // hidePoster?.();
    // guiSlot.style.display = 'block';

    this.status = AdUnitStatus.PLAYING;
    // this._startTimestamp = Date.now();

    this._trackEvent(event.Ad.AdVideoStart);

    this._runUpdateGUI();
  }

  _onVideoFirstQuartile = () => {
    if (!document.body.contains(this.slot)) return;
    this.logger.log(`[AdUnit ${this.config.params.groupId === TagsGroup.CONTENT ? '(C) ' : ''}- ${this.config.playerConfig.zone} / ${this.config.params.tid}] _onVideoFirstQuartile`, this);
    this._trackEvent(event.Ad.AdVideoFirstQuartile);
  }

  _onVideoMidpoint = () => {
    if (!document.body.contains(this.slot)) return;
    this.logger.log(`[AdUnit ${this.config.params.groupId === TagsGroup.CONTENT ? '(C) ' : ''}- ${this.config.playerConfig.zone} / ${this.config.params.tid}] _onVideoMidpoint`, this);
    this._trackEvent(event.Ad.AdVideoMidpoint);
  }

  _onVideoThirdQuartile = () => {
    if (!document.body.contains(this.slot)) return;
    this.logger.log(`[AdUnit ${this.config.params.groupId === TagsGroup.CONTENT ? '(C) ' : ''}- ${this.config.playerConfig.zone} / ${this.config.params.tid}] _onVideoThirdQuartile`, this);
    this._trackEvent(event.Ad.AdVideoThirdQuartile);
  }

  _onVideoComplete = ({ success = true, isContentUnit = false } = {}) => {
    if (!document.body.contains(this.slot)) return;
    if (this.status !== AdUnitStatus.PLAYING && this.status !== AdUnitStatus.PAUSED) return;

    this.logger.log(`[AdUnit ${this.config.params.groupId === TagsGroup.CONTENT ? '(C) ' : ''}- ${this.config.playerConfig.zone} / ${this.config.params.tid}] _onVideoComplete`, this);

    // if (!this.impressionEmitted) {
    //   logOnServerDebugInfo(JSON.stringify({ contains: document.body.contains(this.slot), documentReadyState: this.slot.contentDocument.readyState, status: this.status, success, isVideo: this.isVideo, onAdStart: this.onAdStartEmitted, layers: this.layers, video: { exist: Boolean(this.yandexVideo), time: this.yandexVideo?.currentTime, duration: this.yandexVideo?.duration, readyState: this.yandexVideo?.readyState } }), this.config.playerConfig.playerVersion);
    // }

    // if (ACTIVE_UNIT_STATUSES.includes(this.status)) {
    //   this.emitter.emit(event.Player.ToggleControls, true);
    // }

    if (this.timers.checkAlive) {
      clearTimeout(this.timers.checkAlive);
      this.timers.checkAlive = null;
    }
    if (this.timers.checkVolume) {
      clearTimeout(this.timers.checkVolume);
      this.timers.checkVolume = null;
    }
    if (this.timers.yandexRTBImpression) {
      clearTimeout(this.timers.yandexRTBImpression);
      this.timers.yandexRTBImpression = null;
    }
    if (this.timers.quartileApproxDurationCheck) {
      clearTimeout(this.timers.quartileApproxDurationCheck);
      this.timers.quartileApproxDurationCheck = null;
    }
    if (this.timers.invalidRemainingTimeError) {
      clearTimeout(this.timers.invalidRemainingTimeError);
      this.timers.invalidRemainingTimeError = null;
    }
    if (this.timers.durationAd) {
      clearTimeout(this.timers.durationAd);
      this.timers.durationAd = null;
    }
    if (this.timers.checkIFrameAlive) {
      clearInterval(this.timers.checkIFrameAlive);
      this.timers.checkIFrameAlive = null;
    }

    if (this.config.params.groupId === TagsGroup.CONTENT) this.emitter.emit(event.TagsContent.EmitEvent, { unit: this, evt: event.Ad.AdVideoComplete });

    success && !isContentUnit && this._trackEvent(event.Ad.AdVideoComplete);
    this._updateTimeline(0, 0);
    this.emitter.emit(event.Stream.HideSkipButton);

    const { playerConfig, params } = this.config;

    if ((playerConfig.content.type === ContentType.NONE && (!this.config.yaContinuousRTB || params.platform !== Platform.YANDEX)) || (this.config.playerConfig.content.type === ContentType.TAGS && !isContentUnit)) { // || playerConfig.content.type === ContentType.PRELOADER
      this.emitter.emit(event.Player.PlayerVisibilityChangeRequest, false);
    }

    if (playerConfig.content.type === ContentType.VIDEO && playerConfig.type !== PlayerType.EMBED) {
      this.emitter.emit(event.Player.ContentStartRequest, { fullBlock: false });
    }

    if (params.platform !== Platform.GOOGLE) {
       const { groupId } = params;

      // this.status = AdUnitStatus.FINISHED;

      if (groupId === TagsGroup.YANDEX || groupId === TagsGroup.ADMAN) { // || groupId === TagsGroup.CONTENT
        this.queueManager.reuseUnit(this);
      }

      this.emitter.emit(event.Ad.AdVideoComplete, { advID: this.advID, unit: this });
      this.emitter.emit(event.UI.ResetVolumeState);

      if (
          (playerConfig.content.type === ContentType.NONE || playerConfig.content.type === ContentType.PRELOADER) &&
          params.platform === Platform.YANDEX &&
          playerConfig.yaContinuousRTB &&
          this.config.params.groupId !== TagsGroup.CONTENT
      ) {
        this.queueManager.prevActiveUnit = this;
        this.emitter.emit(event.Player.YandexAdFinished);
      } else {
        // this.emitter.emit(event.Player.PlayerVisibilityChangeRequest, false);
        this.remove();
      }

      this.status = AdUnitStatus.FINISHED;

      // this.remove();
    }
  }

  _onAllAdComplete = () => {
    if (!document.body.contains(this.slot)) return;
    if (this.config.params.platform === Platform.GOOGLE) {
      this.status = AdUnitStatus.FINISHED;
      this.remove();
    }
  }

  _onVideoPause = () => {
    if (!document.body.contains(this.slot) || (this.status === AdUnitStatus.FINISHED || this.status === AdUnitStatus.ERROR)) return;
    this.logger.log(`[AdUnit ${this.config.params.groupId === TagsGroup.CONTENT ? '(C) ' : ''}- ${this.config.playerConfig.zone} / ${this.config.params.tid}] _onVideoPause`, this);
    this.status = AdUnitStatus.PAUSED;
  }

  _onVideoResume = () => {
    if (!document.body.contains(this.slot) || (this.status === AdUnitStatus.FINISHED || this.status === AdUnitStatus.ERROR)) return;
    this.logger.log(`[AdUnit ${this.config.params.groupId === TagsGroup.CONTENT ? '(C) ' : ''}- ${this.config.playerConfig.zone} / ${this.config.params.tid}] _onVideoResume`, this);
    this.status = AdUnitStatus.PLAYING;
  }

  set status(status) {
    if (this.status === AdUnitStatus.FINISHED || this.status === AdUnitStatus.ERROR) return;

    this._status = status;
    this.emitter.emit(event.Player.AdUnitStatusChanged, { unit: this, status });
  }

  get status() {
    return this._status;
  }

  start() {
    this.logger.log('method start not implemented');
  }
  pause() {
    this.logger.log('method pause not implemented');
  }
  resume() {
    this.logger.log('method resume not implemented');
  }
  remove() { // TODO: add this.removed flag
    if (this.slot) {
      if (this.slot !== this.queueManager.prevActiveUnit?.slot) this.slot.classList.remove('active');
      if (!this.inFrame) { this.slot.style.zIndex = '-1'; }
      if (this.inFrame && this.scope) this.scope.removeSlot(this.advID);
    }

    clearInterval(this.timers.debug_skip);
    this.impressionDetector.unsubscribe({ event: 'onViewabilityIntervalDone', callback: this.onViewabilityIntervalDone });

    if (this.config.params.platform === Platform.YANDEX) {
      this.emitter.off(event.Player.ViewabilityStatusChanged, this._onViewabilityStatusChanged);
      this.emitter.emit(event.Player.YandexAdFinished);
    }

    if (this._onMessage) window.removeEventListener('message', this._onMessage);

   Object.values(this.timers).forEach(timeout => clearTimeout(timeout));

    // if (this.timers.initVPAID) {
    //   clearTimeout(this.timers.initVPAID);
    //   this.timers.initVPAID = null;
    // }
    // if (this.initTimer) {
    //   clearTimeout(this.initTimer);
    //   this.initTimer = null;
    // }
    // clearTimeout(this.timers.closeFallback);
    // clearTimeout(this.timers.durationAd);

    // if (this.inFrame) {
    //   this.slot.contentDocument.getElementById('ya_rtb').remove();
    //
    //   const div = this.slot.contentDocument.createElement('div');
    //   div.id = 'ya_rtb';
    //
    //   this.slot.contentDocument.body.appendChild(div);
    // }

    setTimeout(() => {
      if (this.slot && !this.inFrame) {
        this.slot.remove();
        this.slot = null;
        // console.log(`slot = null (${this.config.params.tid})`);
      }
      this._unsubscribeEvents();

      if (this.iframe) this.iframe = null;
      if (this.vpaid) this.vpaid = null;
      if (this.scope) this.scope = null;
      if (this.window) this.window = null;
      if (this.videoSlot) this.videoSlot = null;
    }, this.config.timeouts.cleanup);

    // this.logger.log('method remove not implemented');
  }
  resize = () => {
    this.logger.log('method resize not implemented');
  }
  skip = () => {
    // this.logger.log('method skip not implemented');
    this.logger.log(`[AdUnit ${this.config.params.groupId === TagsGroup.CONTENT ? '(C) ' : ''}- ${this.config.playerConfig.zone} / ${this.config.params.tid}] skip`, this);

    this.localEmitter.emit(event.Ad.AdVideoComplete, { success: false });
  }
}

export default AdUnit;


// https://vd164.okcdn.ru/?expires=1740654516005&srcIp=217.119.27.225&pr=55&srcAg=SAFARI_IPHONE_OTHER&ms=45.136.21.11&type=3&sig=7q8K1iBEh4M&ct=0&urls=185.226.55.49&clientType=28&id=8171029465647
// https://vd164.okcdn.ru/?expires=1740654516005&srcIp=217.119.27.225&pr=55&srcAg=SAFARI_IPHONE_OTHER&ms=45.136.21.11&type=3&sig=7q8K1iBEh4M&ct=0&urls=185.226.55.49&clientType=28&id=8171029465647